import React from 'react';
import PropTypes from 'prop-types';

import {Button, Divider, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import {FEEDBACK_STATES, FOLLOW_UP_STATES} from '../../utils/constants';
import {HappinessScoreCard, ImprovementCard, FollowUpCard, PrivacyCheckbox} from './components';

const FormSection = (props) => {
  const {
    handleSendFeedback,
    isSubmitLoading,
    caregiverHappinessQueryParam,
  } = props;

  const getInitialSelection = () => {
    if (caregiverHappinessQueryParam && Object.values(FEEDBACK_STATES).includes(caregiverHappinessQueryParam.toUpperCase())) {
      return caregiverHappinessQueryParam.toUpperCase();
    }
    return FEEDBACK_STATES.UNSELECTED;
  };

  const [caregiverHappinessResponse, setCaregiverHappinessResponse] = React.useState(getInitialSelection());
  const [carequiltHappinessResponse, setCarequiltHappinessResponse] = React.useState(FEEDBACK_STATES.UNSELECTED);
  const [improvementTopicResponses, setImprovementTopicResponses] = React.useState([]);
  const [additionalComments, setAdditionalComments] = React.useState('');
  const [isFollowUpRequired, setIsFollowUpRequired] = React.useState(false);
  const [bestFollowUpTime, setBestFollowUpTime] = React.useState(FOLLOW_UP_STATES.UNSELECTED);
  const [isFeedbackPrivacyRequired, setIsFeedbackPrivacyRequired] = React.useState(false);

  const footerRef = React.useRef(null);
  const [footerHeight, setFooterHeight] = React.useState(0);

  const isFollowUpSectionShowing = React.useMemo(() => {
    const shouldFollowUpSectionShow = caregiverHappinessResponse === FEEDBACK_STATES.UNHAPPY || carequiltHappinessResponse === FEEDBACK_STATES.UNHAPPY;
    if (!shouldFollowUpSectionShow) {
      setIsFollowUpRequired(false);
      setBestFollowUpTime(FOLLOW_UP_STATES.UNSELECTED);
    }
    return shouldFollowUpSectionShow;
  }, [caregiverHappinessResponse, carequiltHappinessResponse]);

  const hasProvidedMinFb = React.useMemo(() => {
    if (
      caregiverHappinessResponse !== FEEDBACK_STATES.UNSELECTED ||
      carequiltHappinessResponse !== FEEDBACK_STATES.UNSELECTED ||
      improvementTopicResponses.length > 0 ||
      additionalComments.trim() !== ''
    ) {
      return true;
    } else {
      return false;
    }
  }, [caregiverHappinessResponse, carequiltHappinessResponse, improvementTopicResponses, additionalComments]);

  React.useLayoutEffect(() => {
    if (!footerRef.current) return;

    const updateFooterHeight = () => {
      if (footerRef.current) {
        setFooterHeight(footerRef.current.offsetHeight);
      }
    };

    // Set initial height
    updateFooterHeight();

    // Watch for size changes
    const resizeObserver = new ResizeObserver(updateFooterHeight);
    resizeObserver.observe(footerRef.current);

    // Cleanup (only runs when component unmounts)
    return () => resizeObserver.disconnect();
  }, []);


  const onSendFeedbackClicked = () => {
    const feedbackData = {
      caregiverHappinessResponse,
      carequiltHappinessResponse,
      improvementResponses: improvementTopicResponses,
      additionalComments,
      isFollowUpRequired,
      bestFollowUpTime,
      isFeedbackPrivacyRequired,
    };
    handleSendFeedback(feedbackData);
  };

  return (
    <>
      <Stack
        alignItems="center"
        sx={{
          width: '100%',
          minHeight: '100vh',
          position: 'relative',
          pb: `${footerHeight}px`,
        }}
      >
        <Stack
          spacing={{
            xs: 2,
            md: 3,
            lg: 3.5,
          }}
          sx={{
            width: {
              xs: '100%',
              md: '80%',
              lg: '66%',
              xl: '50%',
            },
            mx: 'auto',
            my: {
              xs: 2,
              md: 3,
              lg: 3.5,
            },
            px: 1,
          }}
        >
          <HappinessScoreCard
            questionTitleKey='eventFeedback.CaregiverHappinessQuestion'
            happinessScore={caregiverHappinessResponse}
            setHappinessScore={setCaregiverHappinessResponse}
          />
          <HappinessScoreCard
            questionTitleKey='eventFeedback.CareQuiltHappinessQuestion'
            happinessScore={carequiltHappinessResponse}
            setHappinessScore={setCarequiltHappinessResponse}
          />
          <ImprovementCard
            improvementTopicResponses={improvementTopicResponses}
            setImprovementTopicResponses={setImprovementTopicResponses}
            additionalComments={additionalComments}
            setAdditionalComments={setAdditionalComments}
          />
          {/* Follow Up Card */}
          {
            isFollowUpSectionShowing &&
            <FollowUpCard
              isFollowUpRequired={isFollowUpRequired}
              setIsFollowUpRequired={setIsFollowUpRequired}
              bestFollowUpTime={bestFollowUpTime}
              setBestFollowUpTime={setBestFollowUpTime}
            />
          }
          <PrivacyCheckbox
            isFeedbackPrivacyRequired={isFeedbackPrivacyRequired}
            setIsFeedbackPrivacyRequired={setIsFeedbackPrivacyRequired}
          />
        </Stack>
      </Stack>
      {/* Fixed Send Feedback Footer */}
      <Stack
        ref={footerRef}
        bgcolor="color.white"
        sx={{
          width: '100%',
          position: 'fixed',
          left: 0,
          bottom: 0,
          zIndex: 1200,
          boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <Divider />
        <Stack
          alignItems="center"
          spacing={1}
          sx={{
            width: '100%',
            px: 6,
            py: 2,
          }}
        >
          <Button
            onClick={onSendFeedbackClicked}
            variant="contained"
            loading={isSubmitLoading}
            fullWidth
            disabled={!hasProvidedMinFb}
            sx={{
              fontSize: '1.1rem',
              color: 'color.white',
              textTransform: 'none',
              backgroundColor: '#10a4a0',
              borderRadius: '12px',
              px: 3,
              py: 2,
            }}
          >
            <Translate expression='eventFeedback.SendFeedback'/>
          </Button>
          {
            !hasProvidedMinFb &&
            <Typography
              sx={{
                color: 'grey.800',
                fontSize: '0.85rem',
              }}
            >
              <Translate expression='eventFeedback.SendFeedbackPrompt'/>
            </Typography>
          }
        </Stack>
      </Stack>
    </>
  );
};

FormSection.propTypes = {
  handleSendFeedback: PropTypes.func.isRequired,
  isSubmitLoading: PropTypes.bool.isRequired,
  caregiverHappinessQueryParam: PropTypes.string,
};

export default React.memo(FormSection);
